import { gql } from 'apollo-boost';

import { campaignBidModifiersAdjustmentFragment, commonBidModifiersAdjustmentFragment } from './bidModifiers';
import { vCardCampaignDataFragment } from './common';
import { campaignStrategyBudgetFragment, metroStationsFragment, vCardDataFragment } from './domain';
import { allowedBidModifiersValuesFragment } from './rateCorrections';

const emailSettingsFragment = gql`
    fragment emailSettingsFragment on GdCampaignEmailSettings {
        allowedEvents
        checkPositionInterval
        email
        sendAccountNews
        stopByReachDailyBudget
        warningBalance
        xlsReady
    }
`;

const smsSettingsFragment = gql`
    fragment smsSettingsFragment on GdCampaignSmsSettings {
        smsTime {
            endTime {
                hour
                minute
            }
            startTime {
                hour
                minute
            }
        }
        events {
            checked
            event
        }
    }
`;

export const campaignsNotificationFragment = gql`
    fragment campaignsNotificationFragment on GdCampaignNotification {
        emailSettings {
            ...emailSettingsFragment
        }
        smsSettings {
            ...smsSettingsFragment
        }
    }

    ${emailSettingsFragment}
    ${smsSettingsFragment}
`;

export const campaignsNotificationFragments = gql`
    fragment campaignsNotificationFragments on GdDefaultCampaignNotification {
        campaignType
        emailSettings {
            ...emailSettingsFragment
        }
        smsSettings {
            ...smsSettingsFragment
        }
    }

    ${emailSettingsFragment}
    ${smsSettingsFragment}
`;

const campaignVerboseStrategyFragment = gql`
    fragment campaignVerboseStrategyFragment on GdCampaignFlatStrategy {
        strategyType
        isAutoBudget
        platform
        budget {
            ...campaignStrategyBudgetFragment
        }
        ... on GdCampaignStrategyManual {
            separateBidding
        }
        ... on GdStrategyOptimizeClicks {
            avgBid
            bid
            clicksLimit
        }
        ... on GdStrategyOptimizeConversions {
            goalId
            avgCpa
            bid
            payForConversion
        }
        ... on GdCampaignStrategyRoi {
            goalId
            profitability
            reserveReturn
            roiCoef
            bid
        }
    }

    ${campaignStrategyBudgetFragment}
`;

export const campaignsStatusFragment = gql`
    fragment campaignsStatusFragment on GdCampaignStatus {
        allowDomainMonitoring
        primaryStatus
        hasRunningUnmoderatedAds
    }
`;

export const textCampaignFragment = gql`
    fragment TextCampaignFragment on GdTextCampaign {
        contentLanguage
        disabledPlaces
        meaningfulGoals {
            goalId
            conversionValue
        }
        metrikaCounters
        hasTitleSubstitution
        clientDialogId
        hasExtendedGeoTargeting
        abSegmentRetargetingCondition {
            isMetrikaAvailable
            retargetingCondition {
                retargetingConditionId
                conditionRules {
                    sectionId
                    goals {
                        id
                    }
                }
            }
        }
        abSegmentStatisticRetargetingCondition {
            isMetrikaAvailable
            retargetingCondition {
                retargetingConditionId
                conditionRules {
                    sectionId
                    goals {
                        id
                    }
                }
            }
        }
        allowedPageIds
        attributionModel
        hasAddMetrikaTagToUrl
        hasAddOpenstatTagToUrl
        excludePausedCompetingAds
        enableCompanyInfo
        minusKeywords
        broadMatch {
            broadMatchFlag
            broadMatchGoalId
            broadMatchLimit
        }
        commonVcard {
            ...VCardCampaignDataFragment
        }
        isAloneTrafaretAllowed
        defaultPermalinkId
    }
    ${vCardCampaignDataFragment}
`;

export const priceCampaignFragment = gql`
    fragment PriceCampaignFragment on GdPriceCampaign {
        pricePackageId
        flightTargetingsSnapshot {
            geoExpanded
            geoType
            behaviors
            viewTypes
        }
        flightOrderVolume
        flightStatusApprove
        flightStatusCorrect
    }
`;

export const contentPromotionCampaignFragment = gql`
    fragment ContentPromotionCampaignFragment on GdContentPromotionCampaign {
        contentLanguage
        metrikaCounters
        hasExtendedGeoTargeting
        attributionModel
        minusKeywords
    }
`;

export const campaignConstantsFragment = gql`
    fragment campaignConstantsFragment on GdDirectConstants {
        validation {
            campaignConstants {
                maxDisabledPlacesCount
                maxMetrikaCountersNumberForTextCampaign
                maxDisabledIpsCount
                minBroadMatchLimit
                maxBroadMatchLimit
                roiCoefMin
                maxNameLength
            }
            minusKeywordsConstants {
                normalizedMinusKeywordsMaxLength
            }
        }
        defaultValues {
            campaign {
                defaultMeaningfulGoalId
                defaultBroadMatchAllGoalsId
                defaultContextLimit
                defaultDayBudget
                defaultHasEnableCpcHold
                defaultIsAloneTrafaretAllowed
            }
        }
        strategyConstants {
            byAllGoalsGoalId
            meaningfulGoalsOptimizationGoalId
            payForConversionSumToAvgCpaMinRatio
        }
    }
`;

export const defaultCampaignsEditQuery = gql`
    query DefaultCampaignsEditQuery(
        $login: String!
        $campaignTypes: [GdCampaignTypeInput!]
        $city: [GdMetroCityInput!]
    ) {
        reqId: getReqId
        operator {
            access {
                availableFields {
                    campaignFields
                }
            }
        }
        client(searchBy: { login: $login }) {
            notification {
                smsPhone
            }
            clientConstants {
                allowedBidModifierTypesByCampaignTypes {
                    campaignType
                    allowedBidModifierTypes
                }
                allowedBidModifiersValues {
                    ...allowedBidModifiersValuesFragment
                }
            }
            campaignsCount
            defaultCampaignNotifications(campaignTypes: $campaignTypes) {
                ...campaignsNotificationFragments
            }
            features {
                hasAbSegments
                isTycoonOrganizationsEnabled
                isCampaignDefaultOrganizationsEnabled
            }
        }
        constants {
            defaultValues {
                campaigns(campaignTypes: $campaignTypes) {
                    allowedPageIds
                    attributionModel
                    bidModifiers {
                        id
                        enabled
                        type
                        adjustments {
                            ...commonBidModifiersAdjustmentFragment
                            ...campaignBidModifiersAdjustmentFragment
                        }
                    }
                    broadMatch {
                        broadMatchFlag
                        broadMatchGoalId
                        broadMatchLimit
                    }
                    campaignName
                    clientDialogId
                    commonVcard {
                        ...VCardDataFragment
                    }
                    contentLanguage
                    defaultAddMetrikaTagToUrl
                    defaultAddOpenstatTagToUrl
                    defaultBroadMatchAllGoalsId
                    defaultBroadMatchLimit
                    defaultCheckPositionInterval
                    defaultContextLimit
                    defaultDayBudget
                    defaultDayBudgetShowMode
                    defaultEnableCompanyInfo
                    defaultExcludePausedCompetingAds
                    defaultHasEnableCpcHold
                    defaultMeaningfulGoalId
                    defaultSmsTimeInterval {
                        endTime {
                            hour
                            minute
                        }
                        startTime {
                            hour
                            minute
                        }
                    }
                    defaultTimeTarget {
                        enabledHolidaysMode
                        holidaysSettings {
                            endHour
                            isShow
                            rateCorrections
                            startHour
                        }
                        timeBoard
                        useWorkingWeekends
                        idTimeZone
                    }
                    defaultWarningBalance
                    disabledIps
                    disabledPlaces
                    hasExtendedGeoTargeting
                    hasSiteMonitoring
                    hasTitleSubstitute
                    markMetrikaCunters
                    menaningfulGoals {
                        conversionValue
                        goalId
                    }
                    metrikaCounters
                    openStat
                    startDate
                    strategy {
                        ...campaignVerboseStrategyFragment
                    }
                    defaultIsAloneTrafaretAllowed
                }
            }
            ...campaignConstantsFragment
            metroStations(input: { filter: { metroCityIn: $city } }) {
                ...MetroStationsFragment
            }
        }
    }
    ${campaignConstantsFragment}
    ${commonBidModifiersAdjustmentFragment}
    ${campaignBidModifiersAdjustmentFragment}
    ${allowedBidModifiersValuesFragment}
    ${campaignVerboseStrategyFragment}
    ${campaignsNotificationFragments}
    ${metroStationsFragment}
    ${vCardDataFragment}
`;

export const broadmatchGoalsQuery = gql`
    query BroadmatchGoalsQuery($login: String!, $broadMatchGoalsInput: GdBroadMatchesContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            broadMatchGoals(input: $broadMatchGoalsInput) {
                campaignGoals {
                    campaignId
                    goals {
                        id
                        simpleName
                        parentId
                    }
                }
                isMetrikaAvailable
            }
        }
    }
`;

export const campaignsEditDataQuery = gql`
    query CampaignsEditDataQuery($login: String!, $campaignInput: GdCampaignsContainerInput!) {
        reqId: getReqId
        operator {
            access {
                availableFields {
                    campaignFields
                }
            }
        }
        client(searchBy: { login: $login }) {
            campaigns(input: $campaignInput) {
                rowset {
                    id
                    exportId
                    name
                    type
                    access {
                        canEdit
                    }
                    strategy {
                        ...campaignVerboseStrategyFragment
                    }
                    contextLimit
                    hasEnableCpcHold
                    startDate
                    endDate
                    bidModifiers {
                        id
                        enabled
                        type
                        adjustments {
                            ...commonBidModifiersAdjustmentFragment
                            ...campaignBidModifiersAdjustmentFragment
                        }
                    }
                    disabledIps
                    notification {
                        ...campaignsNotificationFragment
                    }
                    status {
                        ...campaignsStatusFragment
                    }
                    timeTarget {
                        enabledHolidaysMode
                        holidaysSettings {
                            endHour
                            startHour
                            isShow
                            rateCorrections
                        }
                        idTimeZone
                        timeBoard
                        useWorkingWeekends
                    }
                    ...TextCampaignFragment
                    ...ContentPromotionCampaignFragment
                    ...PriceCampaignFragment
                }
            }
            notification {
                smsPhone
            }
            clientConstants {
                allowedBidModifierTypesByCampaignTypes {
                    campaignType
                    allowedBidModifierTypes
                }
                allowedBidModifiersValues {
                    ...allowedBidModifiersValuesFragment
                }
            }
            features {
                isTycoonOrganizationsEnabled
                isCampaignDefaultOrganizationsEnabled
            }
        }
        constants {
            ...campaignConstantsFragment
        }
    }
    ${allowedBidModifiersValuesFragment}
    ${campaignVerboseStrategyFragment}
    ${campaignsNotificationFragment}
    ${campaignsStatusFragment}
    ${textCampaignFragment}
    ${contentPromotionCampaignFragment}
    ${priceCampaignFragment}
    ${commonBidModifiersAdjustmentFragment}
    ${campaignBidModifiersAdjustmentFragment}
    ${campaignConstantsFragment}
`;

export const campaignsAvailableForKeyGoalsQuery = gql`
    query CampaignsAvailableForKeyGoalsQuery(
        $login: String!
        $availableForKeyGoalsInput: GdMeaningfulGoalsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            meaningfulGoals(input: $availableForKeyGoalsInput) {
                isMetrikaAvailable
                campaignGoals {
                    campaignId
                    goals {
                        id
                        domain
                        parentId
                        simpleName
                    }
                }
            }
        }
    }
`;

export const campaignsAvailableForKeyGoalsForNewCampaignQuery = gql`
    query CampaignsAvailableForKeyGoalsForNewCampaignQuery($login: String!, $availableForKeyGoalsInput: [Long!]!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            goalsByCounterIds(input: $availableForKeyGoalsInput) {
                isMetrikaAvailable
                counterWithGoals {
                    counterId
                    goals {
                        domain
                        id
                        parentId
                        simpleName
                    }
                }
            }
        }
    }
`;

export const campaignsABSegmentsQuery = gql`
    query CampaignsABSegmentsQuery($login: String!, $abSegmentsInput: GdMeaningfulGoalsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            abSegments(input: $abSegmentsInput) {
                isMetrikaAvailable
                campaignGoals {
                    campaignId
                    goals {
                        sectionName
                        sectionId
                        id
                        name
                        percent
                    }
                }
            }
        }
    }
`;

export const campaignsABSegmentsQueryForNewCampaign = gql`
    query CampaignsABSegmentsQueryForNewCampaign($login: String!, $abSegmentsInput: [Long!]!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            abSegmentsByCounterIds(input: $abSegmentsInput) {
                isMetrikaAvailable
                counterWithGoals {
                    goals {
                        sectionName
                        sectionId
                        id
                        name
                        percent
                    }
                }
            }
        }
    }
`;
